/**
 * Filling a template with what the composer knows.
 *
 * Pure, and outside the views so the backend's test runner can pin it: the
 * substitution is the one place a wrong name could be sent to a real
 * person, and "Hola Daniela," to somebody called Karen is the failure this
 * file exists to prevent.
 *
 * `{{nombre}}` takes the first word of the recipient's name; `{{empresa}}`
 * the employer's; `{{enlace_ficha}}` the link with which that employer can
 * claim the company record we already hold (`config/company_claim.ts`). A
 * value the caller does not know leaves the placeholder visible — a thing to
 * fix before sending — except in the greeting, where "Hola {{nombre}}," becomes
 * "Hola," rather than shipping braces in the one line every recipient reads
 * first.
 *
 * The claim link is never guessed. The composer passes one only when the
 * employer resolves to exactly one company record that nobody manages yet; an
 * employer with no record, two candidate records, or an owner already, keeps
 * the placeholder, and the composer says why.
 */
export function fillTemplate(
    text: string,
    values: { recipientName?: string | null; employerName?: string | null; claimUrl?: string | null }
): string {
    const first = (values.recipientName ?? "").trim().split(/\s+/)[0] ?? "";
    const employer = (values.employerName ?? "").trim();
    const claimUrl = (values.claimUrl ?? "").trim();
    let out = text;
    if (first) out = out.replace(/\{\{\s*nombre\s*\}\}/g, first);
    if (employer) out = out.replace(/\{\{\s*empresa\s*\}\}/g, employer);
    if (claimUrl) out = out.replace(/\{\{\s*enlace_ficha\s*\}\}/g, claimUrl);
    if (!first) out = out.replace(/^(\s*¡?Hola),?\s*\{\{\s*nombre\s*\}\}\s*([,!])/m, "$1$2");
    return out;
}

/** Whether a draft still carries a hole the operator has to fill. */
export function hasUnfilledPlaceholder(text: string): boolean {
    return /\{\{\s*(nombre|empresa|enlace_ficha)\s*\}\}/.test(text);
}

/** Whether a draft still carries the claim link's placeholder specifically. */
export function hasUnfilledClaimLink(text: string): boolean {
    return /\{\{\s*enlace_ficha\s*\}\}/.test(text);
}
